import {
  animate,
  m,
  useMotionTemplate,
  useMotionValue,
  useReducedMotion,
  useSpring,
  useTransform,
} from 'motion/react';
import { useEffect, useRef, type ReactNode } from 'react';

/**
 * Signature ticket (DESIGN §2.1): gold foil, perforated tear line, embossed number,
 * light reflection that follows tilt (pointer or device motion), spring flip on award.
 */
export function TicketCard({
  flipKey,
  children,
  stub,
  className,
}: {
  /** Increment to play the award flip. */
  flipKey: number;
  children: ReactNode;
  stub: ReactNode;
  className?: string;
}) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const tiltX = useSpring(0, { stiffness: 150, damping: 20 });
  const tiltY = useSpring(0, { stiffness: 150, damping: 20 });
  const flip = useMotionValue(0);
  const rotateY = useTransform([tiltY, flip], ([ty, f]) => (ty as number) + (f as number));
  const shine = useTransform(tiltY, [-12, 12], [10, 90]);
  const shineAngle = useTransform(tiltX, [-10, 10], [150, 210]);
  const sheen = useMotionTemplate`conic-gradient(from ${shineAngle}deg at ${shine}% 40%, transparent 0deg, rgb(255 255 255 / 0.32) 30deg, transparent 75deg, transparent 360deg)`;

  // Pointer tilt (desktop / touch drag) and device orientation (phones), both gentle.
  useEffect(() => {
    if (reduce) return;
    const el = ref.current;
    if (!el) return;
    const onMove = (e: PointerEvent) => {
      const r = el.getBoundingClientRect();
      tiltY.set(((e.clientX - r.left) / r.width - 0.5) * 16);
      tiltX.set(-((e.clientY - r.top) / r.height - 0.5) * 12);
    };
    const reset = () => {
      tiltX.set(0);
      tiltY.set(0);
    };
    const onOrient = (e: DeviceOrientationEvent) => {
      if (e.gamma == null || e.beta == null) return;
      tiltY.set(Math.max(-10, Math.min(10, e.gamma / 3)));
      tiltX.set(Math.max(-8, Math.min(8, (e.beta - 45) / 4)));
    };
    el.addEventListener('pointermove', onMove);
    el.addEventListener('pointerleave', reset);
    window.addEventListener('deviceorientation', onOrient);
    return () => {
      el.removeEventListener('pointermove', onMove);
      el.removeEventListener('pointerleave', reset);
      window.removeEventListener('deviceorientation', onOrient);
    };
  }, [reduce, tiltX, tiltY]);

  useEffect(() => {
    if (flipKey === 0 || reduce) return;
    flip.set(0);
    const a = animate(flip, 360, { type: 'spring', stiffness: 90, damping: 16 });
    return () => a.stop();
  }, [flipKey, flip, reduce]);

  return (
    <div className={`[perspective:900px] ${className ?? ''}`}>
      <m.div
        ref={ref}
        style={{ rotateX: tiltX, rotateY, transformStyle: 'preserve-3d' }}
        className="relative overflow-hidden rounded-[var(--radius-card)] text-foil-ink shadow-[0_24px_60px_-20px_rgb(0_0_0/0.9),0_0_0_1px_rgb(255_255_255/0.06)] will-change-transform"
      >
        {/* foil */}
        <div className="absolute inset-0 bg-[image:var(--foil)]" />
        <m.div className="absolute inset-0 mix-blend-overlay" style={{ backgroundImage: sheen }} />
        <div className="absolute inset-0 opacity-[0.18] [background-image:repeating-linear-gradient(90deg,transparent_0_2px,rgb(255_255_255/0.5)_2px_3px)] mix-blend-soft-light" />
        <div className="relative grid grid-cols-[1fr_auto]">
          <div className="min-w-0 px-5 pt-4 pb-5">{children}</div>
          {/* perforated tear line + stub */}
          <div className="relative border-l-2 border-dashed border-foil-ink/25 px-4 py-4">
            <span className="absolute -top-3 -left-3 size-6 rounded-full bg-ink" />
            <span className="absolute -bottom-3 -left-3 size-6 rounded-full bg-ink" />
            {stub}
          </div>
        </div>
      </m.div>
    </div>
  );
}
